/**
 * Rotating small talk. Each NPC has time-of-day greetings and several topics;
 * every topic offers three answers (warm, curious, playful), each with its own reply.
 */
export const enTalks = {
  chat: '💬 Chat',
  emma: {
    greet: {
      morning: ['Good morning! Did you sleep well?', 'Morning! I brought strawberry milk today.'],
      afternoon: ['Hey! How was class?', 'Hi! Isn’t it a beautiful afternoon?'],
      evening: ['Oh, hi! I’m heading home soon.', 'The sunset is so pretty today!'],
    },
    topics: [
      {
        say: 'I’m learning a new English song. Do you like music?',
        choices: [
          { t: 'I love music! Sing it for me?', reply: 'Okay, just the chorus… la la la! Haha, don’t laugh!' },
          { t: 'What is the song about?', reply: 'It’s about being brave even when you feel small. I really like that.' },
          { t: 'Only if you dance too!', reply: 'Haha! Fine, but you have to dance with me!' },
        ],
      },
      {
        say: 'If you could travel anywhere, where would you go?',
        choices: [
          { t: 'Somewhere with you and our friends!', reply: 'Aww, that’s the best answer. A class trip, then!' },
          { t: 'Where would YOU go?', reply: 'Kyoto, to see the temples. Or the Alps, for the snow!' },
          { t: 'To the moon. Obviously.', reply: 'Haha! Bring snacks. Lots of snacks.' },
        ],
      },
      {
        say: 'I got a lower grade than I hoped on my math test…',
        choices: [
          { t: 'You worked hard. That matters most.', reply: 'Thanks… You’re right. I’ll ask the teacher about the parts I missed.' },
          { t: 'Which part was hard? We can study together.', reply: 'Fractions! Ugh. Okay, study date in the library?' },
          { t: 'Math is just numbers being dramatic.', reply: 'Hahaha! That made me feel better, honestly.' },
        ],
      },
      {
        say: 'My cat knocked my homework off the table this morning.',
        choices: [
          { t: 'Oh no! Is it okay?', reply: 'A bit wrinkly, but fine. My cat looked very proud, though.' },
          { t: 'What’s your cat’s name?', reply: 'Pudding! She’s orange and very round.' },
          { t: 'Your cat wanted to help with homework.', reply: 'Haha, she’s terrible at English, though.' },
        ],
      },
    ],
  },
  rain: {
    greet: {
      morning: ['Morning! I’m on chapter five already.', 'Hi! Want to borrow a book?'],
      afternoon: ['Shh, library voice! Hi!', 'Oh, it’s you! Perfect timing.'],
      evening: ['I stayed late reading again.', 'One more page… okay, ten more pages.'],
    },
    topics: [
      {
        say: 'I’m reading a mystery novel. I think I know who did it!',
        choices: [
          { t: 'Don’t tell me! I want to read it too.', reply: 'My lips are sealed. I’ll lend it to you when I’m done.' },
          { t: 'What clues did you find?', reply: 'A muddy shoe and a missing umbrella. Very suspicious.' },
          { t: 'It was the butler. It’s always the butler.', reply: 'Haha! There isn’t even a butler in this story!' },
        ],
      },
      {
        say: 'Sometimes I feel nervous talking in class.',
        choices: [
          { t: 'Lots of people do. You’re not alone.', reply: 'Really? That helps. Maybe I’ll try raising my hand once tomorrow.' },
          { t: 'What makes it scary?', reply: 'Everyone looking at me. But practising with you helps.' },
          { t: 'Pretend everyone is a potato.', reply: 'Potatoes… okay, that’s silly, but it might work!' },
        ],
      },
      {
        say: 'Which do you prefer: comics or novels?',
        choices: [
          { t: 'Both! Stories are stories.', reply: 'Exactly! Good stories are good stories.' },
          { t: 'Can you recommend one?', reply: 'Try “The Lantern Keeper”. It’s about a girl who looks after a lighthouse.' },
          { t: 'Cookbooks. I like the pictures.', reply: 'Haha! Now I’m hungry.' },
        ],
      },
    ],
  },
  teacher: {
    greet: {
      morning: ['Good morning. Ready for today?', 'Morning! Remember to drink water.'],
      afternoon: ['Hello! How are your classes going?', 'Oh, hello. Busy day?'],
      evening: ['You’re still here? Don’t forget to go home and rest.', 'Good work today.'],
    },
    topics: [
      {
        say: 'Helping others is wonderful, but you need to look after yourself too.',
        choices: [
          { t: 'I will. Thank you, Ms. Lin.', reply: 'Good. Rest, eat well, and ask for help when you need it.' },
          { t: 'How do you look after yourself?', reply: 'A cup of tea, a walk, and an early night. Simple things work.' },
          { t: 'I’ll eat an extra cookie for my health.', reply: 'Haha. One cookie. And a piece of fruit.' },
        ],
      },
      {
        say: 'Class 8-3 is planning a charity sale. Any ideas?',
        choices: [
          { t: 'Homemade cookies! I can bake.', reply: 'Lovely idea. People always buy homemade treats.' },
          { t: 'What will the money be used for?', reply: 'Books for the children’s hospital. Good cause, isn’t it?' },
          { t: 'A teacher dunk tank?', reply: 'Absolutely not. Nice try, though.' },
        ],
      },
      {
        say: 'Did you finish the reading for this week?',
        choices: [
          { t: 'Yes! I liked the ending.', reply: 'Me too. We’ll talk about it in class.' },
          { t: 'Not yet. Is it long?', reply: 'Twenty pages. Read a little each day — that’s the trick.' },
          { t: 'My cat ate it.', reply: 'Hmm, the famous homework-eating cat. I’ve heard of it.' },
        ],
      },
    ],
  },
  chunbo: {
    greet: {
      morning: ['Good morning, young one!', 'Up early, eh? Good for you.'],
      afternoon: ['Afternoon! Staying out of trouble?', 'Hot today, isn’t it?'],
      evening: ['Going home? Watch the traffic.', 'Another day done. Good work.'],
    },
    topics: [
      {
        say: 'When I was your age, I walked an hour to school every day.',
        choices: [
          { t: 'Wow, that’s amazing!', reply: 'And I loved it! I saw rice fields and buffalo every morning.' },
          { t: 'What was school like back then?', reply: 'Small. One classroom, one teacher, lots of noise.' },
          { t: 'Uphill both ways?', reply: 'Haha! Clever kid. Only one way. The other was downhill!' },
        ],
      },
      {
        say: 'I keep a little garden behind my booth.',
        choices: [
          { t: 'Can I help water it sometime?', reply: 'Of course! The tomatoes like company.' },
          { t: 'What do you grow?', reply: 'Tomatoes, basil and one very stubborn pumpkin.' },
          { t: 'Do the plants guard the gate too?', reply: 'Haha! The pumpkin is my deputy.' },
        ],
      },
      {
        say: 'Remember: if a stranger asks you to go somewhere, say no and come find me.',
        choices: [
          { t: 'I will, Uncle Chun.', reply: 'Good. Safety first, always.' },
          { t: 'What if they say they know my mom?', reply: 'Still say no. Your family would never send a stranger without telling you.' },
          { t: 'I’ll do a karate pose and run.', reply: 'Just the running part is fine!' },
        ],
      },
    ],
  },
  librarian: {
    greet: {
      morning: ['Good morning, dear.', 'Ah, an early reader!'],
      afternoon: ['Hello. Looking for something special?', 'Welcome back.'],
      evening: ['We close soon, but take your time.', 'Last chance to borrow today!'],
    },
    topics: [
      {
        say: 'Every book is a door to another world.',
        choices: [
          { t: 'Which door should I open next?', reply: 'Try a book about the ocean. You’ll never see fish the same way.' },
          { t: 'What was your first favourite book?', reply: 'A book about a brave little mouse. I read it thirty times.' },
          { t: 'Do you have a book about doors?', reply: 'Ha! Actually… yes. Architecture, shelf twelve.' },
        ],
      },
      {
        say: 'Please be gentle with the old books. They’re older than me!',
        choices: [
          { t: 'I’ll be careful, I promise.', reply: 'Thank you. Books remember who treats them kindly.' },
          { t: 'How old is the oldest one?', reply: 'Over eighty years! It’s a dictionary with very thin pages.' },
          { t: 'Older than you? Impossible!', reply: 'Oh, you charmer. Off you go, read something!' },
        ],
      },
    ],
  },
  coach: {
    greet: {
      morning: ['Morning! Stretch first!', 'Rise and shine, champion!'],
      afternoon: ['Hey! Want to run a lap?', 'Keep that energy up!'],
      evening: ['Cool down, then go home.', 'Good effort today!'],
    },
    topics: [
      {
        say: 'Sport isn’t about winning. It’s about getting a little better every day.',
        choices: [
          { t: 'I’ll try my best every day.', reply: 'That’s the spirit! Small steps, big results.' },
          { t: 'How do I get faster?', reply: 'Sleep well, eat well, and practise starts. Explode off the line!' },
          { t: 'Can I get better at napping?', reply: 'Ha! Rest is part of training. But only after practice.' },
        ],
      },
      {
        say: 'Drink water before you’re thirsty. Your body will thank you.',
        choices: [
          { t: 'Got it, Coach!', reply: 'Good. Hydration is a superpower.' },
          { t: 'How much should I drink?', reply: 'A cup every hour or so when you’re active.' },
          { t: 'Does bubble tea count?', reply: 'Nice try. Water first!' },
        ],
      },
    ],
  },
  clerk: {
    greet: {
      morning: ['Morning! Fresh bread today.', 'Hi! Early shopping?'],
      afternoon: ['Welcome! Anything I can help with?', 'Hello again!'],
      evening: ['Evening! The rice balls are half price now.', 'Long day? Grab a snack.'],
    },
    topics: [
      {
        say: 'Did you know the store is open until eleven?',
        choices: [
          { t: 'That’s a long shift! Thank you for working hard.', reply: 'Aw, thanks. Customers like you make it easy.' },
          { t: 'What’s the most popular item?', reply: 'Tea eggs! People buy dozens every day.' },
          { t: 'Do you sleep behind the counter?', reply: 'Haha, only in my dreams!' },
        ],
      },
      {
        say: 'I’m saving up to open my own café one day.',
        choices: [
          { t: 'You’ll do it! I’ll be your first customer.', reply: 'Deal! First cup of cocoa is on the house.' },
          { t: 'What would you call it?', reply: '“Little Lantern Café”. Cozy, with lots of plants.' },
          { t: 'Will there be free cookies?', reply: 'For my first customer? Of course!' },
        ],
      },
    ],
  },
  grandma: {
    greet: {
      morning: ['Good morning, little one.', 'Such a fresh morning, isn’t it?'],
      afternoon: ['Come sit with me a moment.', 'The pigeons are hungry today.'],
      evening: ['Time to go home soon.', 'Look how pink the sky is.'],
    },
    topics: [
      {
        say: 'When I was young, this park was all rice fields.',
        choices: [
          { t: 'It must have been beautiful.', reply: 'It was. Green as far as you could see, and frogs singing at night.' },
          { t: 'What did you do for fun?', reply: 'We caught fireflies in jars and let them go again.' },
          { t: 'Did the pigeons live here too?', reply: 'Ha! Their great-great-grandparents, maybe!' },
        ],
      },
      {
        say: 'Do you call your grandparents often?',
        choices: [
          { t: 'I should call them more. I will!', reply: 'They’ll be so happy. A short call means a lot.' },
          { t: 'What do grandparents like to hear?', reply: 'What you ate, what you learned, and that you’re happy.' },
          { t: 'I send them stickers!', reply: 'Stickers are nice. A voice is even nicer.' },
        ],
      },
    ],
  },
  bo: {
    greet: {
      morning: ['Hi! Wanna play?', 'Look! I found a shiny rock!'],
      afternoon: ['Tag! You’re it! …Just kidding.', 'I’m a dinosaur! RAWR!'],
      evening: ['Mom says I have to go home soon…', 'I’m not tired! *yawns*'],
    },
    topics: [
      {
        say: 'When I grow up, I want to be an astronaut!',
        choices: [
          { t: 'You’ll be a great astronaut!', reply: 'Yeah! I’ll wave at you from the moon!' },
          { t: 'What will you do in space?', reply: 'Float! And eat space ice cream!' },
          { t: 'Can you take me with you?', reply: 'Only if you bring snacks!' },
        ],
      },
      {
        say: 'Do you know any jokes?',
        choices: [
          { t: 'Why did the cookie cry? Its mom was a wafer too long!', reply: 'Hahaha! I don’t get it but it’s funny!' },
          { t: 'Do YOU know one?', reply: 'What has four legs and goes boo? …A cow with a cold! Hahaha!' },
          { t: 'Knock knock.', reply: 'Who’s there? …Wait, I wanna do it!' },
        ],
      },
    ],
  },
  mom: {
    greet: {
      morning: ['Good morning, sweetheart. Breakfast is ready.', 'Morning! Did you pack everything?'],
      afternoon: ['Hi, love. How was your day?', 'You’re home! Wash your hands.'],
      evening: ['Dinner will be ready soon.', 'Don’t stay up too late tonight.'],
    },
    topics: [
      {
        say: 'Was there anything hard today you want to talk about?',
        choices: [
          { t: 'A friend was sad. I tried to help.', reply: 'That’s very kind. Just listening is a big help, you know.' },
          { t: 'How do you deal with hard days, Mom?', reply: 'I make tea, take a deep breath, and remember tomorrow is new.' },
          { t: 'The hardest thing was waiting for dinner.', reply: 'Haha! Okay, okay, it’s almost ready.' },
        ],
      },
      {
        say: 'What should we cook this weekend?',
        choices: [
          { t: 'Let’s make dumplings together!', reply: 'Yes! You fold, I’ll make the filling.' },
          { t: 'Can you teach me your soup recipe?', reply: 'Of course. Grandma taught me — now I’ll teach you.' },
          { t: 'Cake for dinner?', reply: 'Cake for dessert. Vegetables for dinner!' },
        ],
      },
      {
        say: 'I’m proud of how responsible you’re becoming.',
        choices: [
          { t: 'Thanks, Mom. I learned it from you.', reply: 'Oh, you’ll make me cry. Come here!' },
          { t: 'What made you proud today?', reply: 'You helped without being asked. That means a lot.' },
          { t: 'Proud enough for extra pocket money?', reply: 'Haha, nice try! Proud, yes. Rich, no.' },
        ],
      },
    ],
  },
  sister: {
    greet: {
      morning: ['Wake up! Play with me!', 'I drew a cat! Look!'],
      afternoon: ['You’re home! Yay!', 'Can we play puzzle?'],
      evening: ['Read me a story?', 'I’m not sleepy… *yawn*'],
    },
    topics: [
      {
        say: 'Is the moon following our car?',
        choices: [
          { t: 'It looks like it! It wants to say hi.', reply: 'Hi, moon! Hehe!' },
          { t: 'It’s really far away, so it seems to follow us.', reply: 'Ooh… Then it follows everyone at the same time?' },
          { t: 'Yes. It’s a very nosy moon.', reply: 'Nosy moon! Hahaha!' },
        ],
      },
      {
        say: 'I had a bad dream about a monster.',
        choices: [
          { t: 'I’m here. You’re safe.', reply: 'Okay… Can you leave the door open a little?' },
          { t: 'What did the monster look like?', reply: 'Big and fluffy… actually it was kind of cute.' },
          { t: 'I’ll fight it with a pillow!', reply: 'Pillow fight! Pillow fight!' },
        ],
      },
    ],
  },
  dad: {
    greet: {
      morning: ['Morning, champ.', 'Good morning! Coffee first.'],
      afternoon: ['Hey there!', 'How’s my favourite kid?'],
      evening: ['Phew, long day. Come sit with me.', 'Dinner together tonight!'],
    },
    topics: [
      {
        say: 'Sorry I work so late sometimes.',
        choices: [
          { t: 'It’s okay. I like when we eat together.', reply: 'Me too. I’ll try to come home early more often.' },
          { t: 'What do you do at work all day?', reply: 'Lots of meetings and spreadsheets. Not as exciting as school!' },
          { t: 'You owe me a game night!', reply: 'Deal! Saturday, board games, no phones.' },
        ],
      },
      {
        say: 'Want to hear a dad joke?',
        choices: [
          { t: 'Go on, Dad.', reply: 'Why don’t eggs tell jokes? They’d crack each other up!' },
          { t: 'Only if it’s actually funny.', reply: 'What do you call a sleeping dinosaur? A dino-snore!' },
          { t: 'Please, no!', reply: 'Too late! Why did the scarecrow win an award? He was outstanding in his field!' },
        ],
      },
    ],
  },
  xiaoyu: {
    greet: {
      morning: ['…Hi.', 'Oh. Morning.'],
      afternoon: ['Hey.', 'Hi… thanks for earlier.'],
      evening: ['I should go home soon.', 'It was an okay day.'],
    },
    topics: [
      {
        say: 'I like drawing. It helps when my head is noisy.',
        choices: [
          { t: 'That’s a great way to feel better.', reply: 'Yeah. Maybe I’ll show you some drawings one day.' },
          { t: 'What do you like to draw?', reply: 'Robots. And sometimes my dog.' },
          { t: 'Draw me as a superhero?', reply: '…Okay. Haha. With a cape.' },
        ],
      },
      {
        say: 'Do you ever feel tired of everything?',
        choices: [
          { t: 'Sometimes. Talking to someone helps.', reply: 'Yeah… Talking to you helps a little.' },
          { t: 'What makes you feel tired?', reply: 'Home is loud. But the counseling room is quiet.' },
          { t: 'Let’s get some cocoa.', reply: 'Cocoa sounds good, actually.' },
        ],
      },
    ],
  },
};
